// =============================================================================
// GstCalculator.jsx — embeddable Australian GST calculator (Resources pages)
// =============================================================================
// A small, self-contained utility that supervisors can drop onto any Resources
// content page by typing the [[gst-calculator]] shortcode into the page body;
// ResourceSection swaps this component in where the token appears. It CAN'T be
// authored as inline HTML+JS because Resources content is sanitised (scripts and
// event handlers are stripped by design), so the live logic lives here as vetted,
// tested code (math in services/gst.ts).
//
// One amount box + a direction toggle:
//   • "Add GST"      → amount is GST-exclusive; show GST (×0.10) and the total.
//   • "Back out GST" → amount is GST-inclusive; show the GST component (÷11) and
//                      the ex-GST base.
// Plus a "No. of Children" dropdown (1–5): invoicing staff often need each figure
// divided evenly per child to enter the split into Unleashed, so a "Per child"
// column shows every amount ÷ the selected count.
// =============================================================================

import React from "react";
import { addGst, extractGst } from "../../services/gst";

function money(n) {
  if (!Number.isFinite(n)) return "$0.00";
  return `$${n.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const WRAP = {
  border: "1px solid var(--color-zephyr-panel-border, #CFD8DC)",
  background: "var(--color-zephyr-panel-bg, #F7F9F9)",
  borderRadius: 10,
  padding: 16,
  margin: "12px 0",
  maxWidth: 460,
};

const CHILD_OPTIONS = [1, 2, 3, 4, 5];
const cellR = { padding: "4px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums" };
const headR = {
  padding: "0 8px 4px",
  textAlign: "right",
  fontSize: 12,
  fontWeight: 700,
  color: "var(--color-zephyr-text, #1A2E2B)",
};

export function GstCalculator() {
  const [amount, setAmount] = React.useState("");
  const [mode, setMode] = React.useState("add"); // "add" | "extract"
  const [children, setChildren] = React.useState(1);

  const num = Number.parseFloat(amount);
  const hasValue = amount.trim() !== "" && Number.isFinite(num);
  const result = mode === "add" ? addGst(num) : extractGst(num);
  const perChild = (n) => n / children;

  // A label + full amount + per-child amount row.
  function Row({ label, value, bold }) {
    return (
      <tr
        style={
          bold
            ? { borderTop: "1px solid var(--color-zephyr-panel-border, #CFD8DC)", fontWeight: 700 }
            : undefined
        }
      >
        <td style={{ padding: "4px 0", opacity: bold ? 1 : 0.75 }}>{label}</td>
        <td style={cellR}>{hasValue ? money(value) : "—"}</td>
        <td style={cellR}>{hasValue ? money(perChild(value)) : "—"}</td>
      </tr>
    );
  }

  return (
    <div style={WRAP} className="not-prose">
      <div
        style={{ fontWeight: 700, color: "var(--color-zephyr-text, #1A2E2B)", marginBottom: 10 }}
      >
        GST calculator
        <span style={{ fontWeight: 400, opacity: 0.7, fontSize: 13 }}> (Australian, 10%)</span>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <label
          style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}
        >
          <input
            type="radio"
            name="gst-mode"
            checked={mode === "add"}
            onChange={() => setMode("add")}
          />
          Amount excludes GST — add 10%
        </label>
        <label
          style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}
        >
          <input
            type="radio"
            name="gst-mode"
            checked={mode === "extract"}
            onChange={() => setMode("extract")}
          />
          Amount includes GST — back it out
        </label>
      </div>

      <div style={{ display: "flex", gap: 16, marginBottom: 4, flexWrap: "wrap" }}>
        <label style={{ display: "block", fontSize: 13 }}>
          Amount
          <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 4 }}>
            <span style={{ fontSize: 15, opacity: 0.7 }}>$</span>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              style={{
                width: 140,
                padding: "6px 8px",
                border: "1px solid var(--color-zephyr-panel-border, #CFD8DC)",
                borderRadius: 6,
                fontSize: 14,
              }}
            />
          </div>
        </label>

        <label style={{ display: "block", fontSize: 13 }}>
          No. of Children
          <div style={{ marginTop: 4 }}>
            <select
              aria-label="Number of children"
              value={children}
              onChange={(e) => setChildren(Number(e.target.value))}
              style={{
                padding: "6px 8px",
                border: "1px solid var(--color-zephyr-panel-border, #CFD8DC)",
                borderRadius: 6,
                fontSize: 14,
                background: "white",
                cursor: "pointer",
              }}
            >
              {CHILD_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </label>
      </div>

      <table style={{ marginTop: 12, borderCollapse: "collapse", fontSize: 14, width: "100%" }}>
        <thead>
          <tr>
            <th />
            <th style={headR}>Amount</th>
            <th style={headR}>Per child</th>
          </tr>
        </thead>
        <tbody>
          <Row label="Ex-GST" value={result.base} />
          <Row label="GST (10%)" value={result.gst} />
          <Row label="Total (inc. GST)" value={result.total} bold />
        </tbody>
      </table>
    </div>
  );
}
